import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ProductCatalogItemVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';

interface CategoriaSeletorV2 {
  key: string;
  name: string;
  products: ProductCatalogItemVM[];
}

/**
 * Escolhe produtos por categoria. Serve tanto para criar um adicional já aplicado
 * em vários produtos quanto para vincular um adicional existente a mais produtos.
 *
 * Não vincula nada: apenas devolve a seleção. Quem chama decide o que fazer, porque
 * criar e vincular têm caminhos diferentes no servidor.
 */
@Component({
  selector: 'app-seletor-produtos-v2',
  templateUrl: './seletor-produtos-v2.component.html',
  styleUrls: ['./seletor-produtos-v2.component.scss']
})
export class SeletorProdutosV2Component implements OnInit, OnChanges {
  @Input() empresa: any;
  /** Produtos que já têm o adicional: aparecem marcados e travados, no lugar deles. */
  @Input() jaVinculados: Array<number | string> = [];
  @Input() preSelecionados: Array<number | string> = [];
  @Input() podeEditar = true;
  @Input() rotuloAcao = 'Continuar';
  @Input() ocupado = false;

  @Output() confirmou = new EventEmitter<ProductCatalogItemVM[]>();
  @Output() cancelou = new EventEmitter<void>();

  loading = true;
  errorMessage = '';
  search = '';
  products: ProductCatalogItemVM[] = [];
  selected: Record<string, boolean> = {};

  constructor(private facade: ProdutosV2FacadeService) {}

  ngOnInit(): void {
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['empresa'] && !changes['empresa'].firstChange) void this.load();
    if (changes['preSelecionados']) this.apliquePreSelecao();
  }

  get vinculados(): Set<string> {
    return new Set((this.jaVinculados || []).map((id) => String(id)));
  }

  get totalVinculados(): number {
    return this.products.filter((p) => p.id != null && this.vinculados.has(String(p.id))).length;
  }

  get selectedProducts(): ProductCatalogItemVM[] {
    const travados = this.vinculados;
    return this.products.filter((p) => p.id != null && this.selected[String(p.id)] && !travados.has(String(p.id)));
  }

  get selectedCount(): number {
    return this.selectedProducts.length;
  }

  /** Todos os produtos do catálogo já têm o adicional: não há o que escolher. */
  /** Sem seleção o botão não pode ficar com um rótulo pela metade ("Vincular a"). */
  get rotuloDoBotao(): string {
    if (!this.selectedCount) return this.rotuloAcao.replace(/\s+a$/i, '').replace(/\s+com$/i, '');
    const nome = this.selectedCount === 1 ? 'produto' : 'produtos';
    return `${this.rotuloAcao} ${this.selectedCount} ${nome}`;
  }

  get tudoVinculado(): boolean {
    return this.products.length > 0 && this.totalVinculados === this.products.length;
  }

  get categories(): CategoriaSeletorV2[] {
    const term = this.normalize(this.search);
    const map = new Map<string, CategoriaSeletorV2>();
    this.products.forEach((product) => {
      if (product.id == null) return;
      if (term && !this.normalize(product.name).includes(term) && !this.normalize(product.category).includes(term)) return;
      const name = product.category || 'Sem categoria';
      const key = product.categoryId != null ? String(product.categoryId) : name;
      const group = map.get(key) || { key, name, products: [] };
      group.products.push(product);
      map.set(key, group);
    });
    return Array.from(map.values())
      .map((group) => ({ ...group, products: group.products.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }

  async load(): Promise<void> {
    if (!this.empresa?.catalogo?.id) return;
    this.loading = true;
    this.errorMessage = '';
    try {
      this.products = await this.facade.carregueCatalogo(this.empresa);
      this.apliquePreSelecao();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Não foi possível carregar os produtos do catálogo.';
    } finally {
      this.loading = false;
    }
  }

  estaVinculado(product: ProductCatalogItemVM): boolean {
    return product.id != null && this.vinculados.has(String(product.id));
  }

  isSelected(product: ProductCatalogItemVM): boolean {
    return this.estaVinculado(product) || (product.id != null && !!this.selected[String(product.id)]);
  }

  toggleProduct(product: ProductCatalogItemVM): void {
    if (product.id == null || this.estaVinculado(product) || !this.podeEditar) return;
    const key = String(product.id);
    if (this.selected[key]) delete this.selected[key];
    else this.selected[key] = true;
  }

  /** Conta só o que dá para escolher; o que já está vinculado não entra na fração. */
  categoriaDisponivel(group: CategoriaSeletorV2): ProductCatalogItemVM[] {
    return group.products.filter((product) => !this.estaVinculado(product));
  }

  categorySelectedCount(group: CategoriaSeletorV2): number {
    return this.categoriaDisponivel(group).filter((product) => this.isSelected(product)).length;
  }

  isCategoryFullySelected(group: CategoriaSeletorV2): boolean {
    const disponiveis = this.categoriaDisponivel(group);
    return disponiveis.length > 0 && this.categorySelectedCount(group) === disponiveis.length;
  }

  toggleCategory(group: CategoriaSeletorV2): void {
    if (!this.podeEditar) return;
    const marcar = !this.isCategoryFullySelected(group);
    this.categoriaDisponivel(group).forEach((product) => {
      if (product.id == null) return;
      if (marcar) this.selected[String(product.id)] = true;
      else delete this.selected[String(product.id)];
    });
  }

  clearSelection(): void {
    this.selected = {};
  }

  confirmar(): void {
    if (!this.selectedCount || this.ocupado) return;
    this.confirmou.emit(this.selectedProducts);
  }

  trackByCategory(_: number, group: CategoriaSeletorV2): string {
    return group.key;
  }

  trackByProduct(_: number, product: ProductCatalogItemVM): any {
    return product.id;
  }

  private apliquePreSelecao(): void {
    (this.preSelecionados || []).forEach((id) => {
      if (id == null) return;
      if (this.products.some((product) => String(product.id) === String(id))) {
        this.selected[String(id)] = true;
      }
    });
  }

  private normalize(value: string | null | undefined): string {
    return (value || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}
